import { zodResolver } from '@hookform/resolvers/zod';
import {
  QuoteShareContactsForm,
  QuoteShareContactsFormSchema,
  QuoteShareResponse,
  RecordShareBaseParams,
} from '@monetize/types/app';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../api/axios';
import { useUpdateQuoteShareContacts } from '../../../api/shareService';
import {
  MButton,
  MCenterModal,
  MCheckbox,
  MFormField,
  MStack,
} from '../../../components/Monetize';
import { logger } from '../../../services/logger';
import Sentry from '../../../services/sentry';
import {
  getRecordShareCaptureContext,
  getRecordShareErrorLogContext,
} from '../../../utils/recordShareLogging';
import { getFormContactsFromResponse } from '../quote-share.utils';
import { QuoteShareContact } from './QuoteShareContact';

interface QuoteShareContactEditModalProps {
  requireAddress: boolean;
  quoteData: QuoteShareResponse;
  authParams: RecordShareBaseParams;
  onClose: () => void;
}

export const QuoteShareContactEditModal = ({
  requireAddress,
  quoteData,
  authParams,
  onClose,
}: QuoteShareContactEditModalProps) => {
  const { mutateAsync: updateContacts, isPending: isSaving } =
    useUpdateQuoteShareContacts();

  const formMethods = useForm<
    QuoteShareContactsForm,
    undefined,
    QuoteShareContactsForm
  >({
    resolver: zodResolver(QuoteShareContactsFormSchema),
    defaultValues: getFormContactsFromResponse(requireAddress, quoteData),
  });

  const { control, handleSubmit, watch } = formMethods;

  const useAsShippingContact = watch('useBillingForShippingContact');

  async function handleSave(contacts: QuoteShareContactsForm) {
    try {
      await updateContacts({
        authParams,
        quoteShareData: quoteData,
        contacts,
      });
      onClose();
    } catch (ex) {
      const logContext = getRecordShareErrorLogContext(authParams, ex);
      Sentry.logger.error('QuoteSharePage - contact update failed', logContext);
      Sentry.captureException(
        ex,
        getRecordShareCaptureContext(logContext, {
          type: 'QUOTE_SHARE',
          action: 'UPDATE_CONTACTS',
        }),
      );
      handleApiErrorToast(ex);
      logger.error(ex);
    }
  }

  return (
    <FormProvider {...formMethods}>
      <form
        id="contact-form"
        onSubmit={handleSubmit(handleSave, (err) => logger.error(err))}
      >
        <MCenterModal
          size="xl"
          isOpen
          onClose={onClose}
          modalTitle="Edit Contact Details"
          closeOnEsc={!isSaving}
          closeOnOverlayClick={false}
          renderFooter={() => (
            <MStack
              gap={4}
              direction="row"
              align="center"
              justify="right"
              flex={1}
            >
              <MButton
                onClick={() => onClose()}
                variant="cancel"
                minW="auto"
                isDisabled={isSaving}
              >
                Cancel
              </MButton>
              <MButton
                data-testid="contact-form-submit"
                variant="primary"
                isLoading={isSaving}
                type="submit"
                form="contact-form"
                minW="auto"
              >
                Save
              </MButton>
            </MStack>
          )}
        >
          <QuoteShareContact
            label="Billing Contact"
            formKeyPrefix="billingContact"
            requireAddress={requireAddress}
          />

          <MFormField label="" skipLabel>
            <Controller
              name="useBillingForShippingContact"
              control={control}
              defaultValue
              render={({ field: { value, ...rest } }) => (
                <MCheckbox isChecked={value} {...rest}>
                  Use as Shipping Contact
                </MCheckbox>
              )}
            />
          </MFormField>

          {!useAsShippingContact && (
            <QuoteShareContact
              label="Shipping Contact"
              formKeyPrefix="shippingContact"
              mt={4}
              requireAddress={requireAddress}
            />
          )}
        </MCenterModal>
      </form>
    </FormProvider>
  );
};
